import { EyeOff, GripVertical } from "lucide-react";
import { useState, type ReactNode } from "react";

/**
 * Envoltorios de edición del layout.
 *
 * El arrastre usa HTML5 nativo en vez de una librería: alcanza para reordenar
 * listas y no agrega dependencias al bundle. La contrapartida está asumida — no
 * funciona por touch y el reordenamiento por teclado no está cubierto. Si el
 * layout pasa a usarse desde tablet, ahí sí conviene `@dnd-kit`.
 *
 * Todo esto solo se monta en modo edición: fuera de él, el reporte se renderiza
 * sin handlers de arrastre ni botones encima del contenido.
 */

interface DragProps {
  id: string;
  onMove: (id: string, targetId: string) => void;
  onHide?: (id: string) => void;
  editing: boolean;
  kind: "section" | "block" | "card";
  label?: string;
  children: ReactNode;
}

export function Reorderable({ id, onMove, onHide, editing, kind, label, children }: DragProps) {
  const [over, setOver] = useState(false);

  if (!editing) return <>{children}</>;

  return (
    <div
      className={`rep-edit rep-edit-${kind}${over ? " is-over" : ""}`}
      draggable
      onDragStart={(event) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", id);
        // El tipo va en el propio dataTransfer para que una card no se pueda
        // soltar sobre una sección y viceversa.
        event.dataTransfer.setData(`application/x-wolf-${kind}`, id);
      }}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes(`application/x-wolf-${kind}`)) return;
        event.preventDefault();
        event.stopPropagation();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes(`application/x-wolf-${kind}`)) return;
        event.preventDefault();
        event.stopPropagation();
        setOver(false);
        const dragged = event.dataTransfer.getData("text/plain");
        if (dragged && dragged !== id) onMove(dragged, id);
      }}
    >
      <div className="rep-edit-bar">
        <span className="rep-edit-grip" title="Arrastrá para reordenar">
          <GripVertical size={13} aria-hidden="true" />
          {label && <em>{label}</em>}
        </span>
        {onHide && (
          <button type="button" className="rep-icon-button" title="Ocultar de mi layout"
            onClick={() => onHide(id)}>
            <EyeOff size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/** Bandeja de lo oculto. Ocultar nunca borra, así que siempre hay vuelta atrás. */
export function HiddenTray({
  hidden,
  labels,
  onShow,
  onReset,
}: {
  hidden: string[];
  labels: Record<string, string>;
  onShow: (id: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="rep-hidden-tray">
      <div>
        <strong>Ocultos ({hidden.length})</strong>
        <button type="button" className="rep-icon-button" onClick={onReset}>
          Restaurar layout original
        </button>
      </div>
      {hidden.length ? (
        <div className="rep-hidden-chips">
          {hidden.map((id) => (
            <button key={id} type="button" onClick={() => onShow(id)} title="Volver a mostrar">
              {labels[id] ?? id}
            </button>
          ))}
        </div>
      ) : (
        <p className="rep-empty">Nada oculto. Usá el ojo tachado sobre cualquier bloque o card.</p>
      )}
    </div>
  );
}
